import { useState, useCallback, useId, useRef, useEffect } from "react";

import { css } from "@compiled/react";

import ChevronDownIcon from "@atlaskit/icon/core/chevron-down";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Button } from "@/libraries/design/components/button/src/button";
import { wacToken } from "@/libraries/design/foundations";

export interface NavItem {
    label: string;
    linkUrl: string;
}

export interface SubNavProps {
    headingLabel: string;
    headingLinkUrl: string;
    navItems: NavItem[];
    cta: {
        label: string;
        linkUrl: string;
    };
    className?: string;
}

const NAV_HEIGHT = 68;

const subNavContainerCss = css({
    insetBlockStart: 0,
    position: "sticky",
    // this is to offset the uniform container top padding, not sure where the
    // best place is to do this
    marginBlockStart: "-80px",
    width: "100%",
    zIndex: 1
});

const subNavWrapperCss = css({
    position: "sticky",
    insetBlockStart: 0,
    width: "100%",
    backgroundColor: wacToken("color.neutral.light0"),
    transition: "box-shadow 200ms ease-in-out, z-index 0ms",
    zIndex: 1,
    // overlap the atlassian logo on mobile because the parent container has too much padding
    marginInlineStart: "-12px",
    [media.above.md]: {
        marginInlineStart: 0,
        marginInlineEnd: 0
    }
});

const subNavWrapperAtTopCss = css({
    zIndex: 10
});

const subNavInnerCss = css({
    display: "flex",
    alignItems: "center",
    height: "64px",
    maxWidth: "1200px",
    marginInline: "auto",
    zIndex: 1,
    // overlap the hamburger menu on mobile because the parent container has too much padding
    marginInlineEnd: "-32px",
    [media.above.md]: {
        marginInlineEnd: 0,
        height: "72px",
        gap: token("space.300")
    }
});

const headingLinkCss = css({
    "color": wacToken("color.primary.black"),
    "textDecoration": "none",
    "fontFamily": wacToken("font.charlie.display"),
    "fontSize": "1.25rem",
    "fontWeight": 600,
    "lineHeight": 1.2,
    "flexShrink": 0,
    "&:hover": {
        color: wacToken("color.extended.blue800")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: token("space.025"),
        borderRadius: token("radius.xsmall")
    },
    [media.above.md]: {
        fontSize: "1.5rem"
    }
});

const desktopNavCss = css({
    display: "none",
    [media.above.md]: {
        display: "flex",
        alignItems: "center",
        width: "100%",
        justifyContent: "space-between",
        gap: token("space.400")
    }
});

const navListCss = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.300"),
    listStyle: "none",
    margin: 0,
    padding: 0
});

const navItemLinkCss = css({
    "color": wacToken("color.primary.black"),
    "textDecoration": "none",
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "1rem",
    "fontWeight": 500,
    "padding": `${token("space.100")} ${token("space.050")}`,
    "borderRadius": token("radius.xsmall"),
    "transition": "color 200ms ease",
    "&:hover": {
        color: wacToken("color.extended.blue800")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: token("space.025")
    }
});

const navItemLinkActiveCss = css({
    color: wacToken("color.extended.blue800"),
    fontWeight: 600,
    borderBlockEnd: `2px solid ${wacToken("color.extended.blue800")}`,
    borderRadius: 0
});

const mobileNavCss = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.200"),
    width: "100%",
    justifyContent: "space-between",
    position: "sticky",
    insetBlockStart: 0,
    [media.above.md]: {
        display: "none"
    }
});

const mobileDropdownButtonCss = css({
    "display": "flex",
    "alignItems": "center",
    "gap": token("space.100"),
    "backgroundColor": "transparent",
    "border": "none",
    "cursor": "pointer",
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "0.875rem",
    "fontWeight": 500,
    "color": wacToken("color.primary.black"),
    "padding": `${token("space.100")} ${token("space.150")}`,
    "borderRadius": token("radius.xsmall"),
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light200")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: token("space.025")
    }
});

const chevronIconCss = css({
    transition: "transform 200ms ease"
});

const chevronIconRotatedCss = css({
    transform: "rotate(180deg)"
});

const mobileDropdownCss = css({
    position: "absolute",
    insetBlockStart: "100%",
    insetInlineStart: 0,
    insetInlineEnd: 0,
    backgroundColor: wacToken("color.neutral.light0"),
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`,
    zIndex: 5,
    [media.above.md]: {
        display: "none"
    }
});

const mobileDropdownListCss = css({
    listStyle: "none",
    margin: 0,
    padding: 0
});

const mobileDropdownItemCss = css({
    "borderBlockEnd": `1px solid ${wacToken("color.neutral.light200")}`,
    "&:last-child": {
        borderBlockEnd: "none"
    }
});

const mobileDropdownLinkCss = css({
    "display": "block",
    "color": wacToken("color.primary.black"),
    "textDecoration": "none",
    "fontFamily": wacToken("font.charlie.text"),
    "fontSize": "1rem",
    "fontWeight": 500,
    "padding": token("space.200"),
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light100"),
        color: wacToken("color.extended.blue800")
    },
    "&:focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: token("space.negative.025")
    }
});

const mobileDropdownLinkActiveCss = css({
    backgroundColor: wacToken("color.neutral.light200"),
    color: wacToken("color.extended.blue800"),
    fontWeight: 600
});

const mobileCTACss = css({
    // explicit white color to make sure the hamburger button doesn't show up behind the transparent
    // background color of the button
    backgroundColor: wacToken("color.neutral.light0")
});

const isLinkActive = (linkUrl: string): boolean => {
    if (typeof window === "undefined") return false;

    const currentPath = window.location.pathname;
    const linkPath = new URL(linkUrl, window.location.origin).pathname;

    return currentPath === linkPath || currentPath.startsWith(linkPath + "/");
};

export const SubNav = ({
    headingLabel,
    headingLinkUrl,
    navItems,
    cta,
    className
}: SubNavProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isAtTop, setIsAtTop] = useState(false);
    const dropdownId = useId();
    const navRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // handle z-index based on scroll position
    // when scrolled >= 68px (nav height), subnav overlaps nav, so set z-index to 10
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY || window.pageYOffset;
            setIsAtTop(scrollY >= NAV_HEIGHT);
        };

        // check initial scroll position
        handleScroll();

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const toggleDropdown = useCallback(() => {
        setIsDropdownOpen((prev) => !prev);
    }, []);

    const closeDropdown = useCallback(() => {
        setIsDropdownOpen(false);
    }, []);

    const handleDropdownKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Escape" && isDropdownOpen) {
                setIsDropdownOpen(false);
            }
        },
        [isDropdownOpen]
    );

    return (
        <div
            ref={containerRef}
            css={[subNavContainerCss, isAtTop && subNavWrapperAtTopCss]}
            className={className}
        >
            <div
                ref={navRef}
                css={[subNavWrapperCss]}
                role="navigation"
                aria-label={`${headingLabel} navigation`}
            >
                <div css={subNavInnerCss}>
                    <a
                        href={headingLinkUrl}
                        css={headingLinkCss}
                        data-testid="subnav-heading-link"
                        data-event="clicked"
                        data-event-component="linkButton"
                        data-event-container="roadmap"
                    >
                        {headingLabel}
                    </a>

                    <div css={desktopNavCss}>
                        <ul css={navListCss} role="list">
                            {navItems.map((item, index) => {
                                const isActive = isLinkActive(item.linkUrl);
                                return (
                                    <li key={index}>
                                        <a
                                            href={item.linkUrl}
                                            css={[
                                                navItemLinkCss,
                                                isActive && navItemLinkActiveCss
                                            ]}
                                            data-testid={`subnav-item-${index}`}
                                            aria-current={
                                                isActive ? "page" : undefined
                                            }
                                            data-event="clicked"
                                            data-event-component="linkButton"
                                            data-event-container="roadmapSubnav"
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                        <Button
                            variant="secondary"
                            href={cta.linkUrl}
                            data-testid="subnav-cta"
                            dataEventProps={{
                                "data-product-key": "roadmap",
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "roadmapSubnav"
                            }}
                        >
                            {cta.label}
                        </Button>
                    </div>

                    <div css={mobileNavCss}>
                        <button
                            type="button"
                            css={mobileDropdownButtonCss}
                            onClick={toggleDropdown}
                            onKeyDown={handleDropdownKeyDown}
                            aria-expanded={isDropdownOpen}
                            aria-controls={dropdownId}
                            aria-haspopup="true"
                            data-testid="subnav-mobile-dropdown-button"
                            data-event="clicked"
                            data-event-component="button"
                            data-event-container="roadmapSubnav"
                        >
                            <span
                                css={[
                                    chevronIconCss,
                                    isDropdownOpen && chevronIconRotatedCss
                                ]}
                            >
                                <ChevronDownIcon
                                    label={
                                        isDropdownOpen
                                            ? "Close navigation menu"
                                            : "Open navigation menu"
                                    }
                                    color="currentColor"
                                />
                            </span>
                        </button>
                        <Button
                            variant="secondary"
                            href={cta.linkUrl}
                            css={mobileCTACss}
                            data-testid="subnav-mobile-cta"
                            dataEventProps={{
                                "data-product-key": "roadmap",
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "roadmapSubnav"
                            }}
                        >
                            {cta.label}
                        </Button>
                    </div>
                </div>

                {isDropdownOpen && (
                    <div
                        id={dropdownId}
                        css={mobileDropdownCss}
                        data-testid="subnav-mobile-dropdown"
                    >
                        <ul css={mobileDropdownListCss} role="list">
                            {navItems.map((item, index) => {
                                const isActive = isLinkActive(item.linkUrl);
                                return (
                                    <li key={index} css={mobileDropdownItemCss}>
                                        <a
                                            href={item.linkUrl}
                                            css={[
                                                mobileDropdownLinkCss,
                                                isActive &&
                                                    mobileDropdownLinkActiveCss
                                            ]}
                                            onClick={closeDropdown}
                                            data-testid={`subnav-mobile-item-${index}`}
                                            aria-current={
                                                isActive ? "page" : undefined
                                            }
                                        >
                                            {item.label}
                                        </a>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

SubNav.displayName = "SubNav";
